<script setup lang="ts">
import { type PropType, computed } from 'vue'
import { cva } from 'class-variance-authority'
import { useI18n } from 'vue-i18n'
import type { PoolFromMappy, PoolStatistic } from '../../../interfaces'
import { BadgeType } from '../../../interfaces/UI'

const { t } = useI18n()

const props = defineProps({
    pool: {
        type: Object as PropType<PoolFromMappy>,
        required: true
    },
    loadingStations: {
        type: Boolean,
        required: true
    },
    chargemapUrl: {
        type: String,
        required: true
    },
    isB2B: {
        type: Boolean,
        default: true
    },
    legacyApiUrl: {
        type: String,
        required: true
    }
})

const poolDetailsUrl = computed<string>(
    () => `${props.chargemapUrl}/${props.pool.slug}.html`
)

const formatAddress = computed<string>(() => {
    const { number, streetName, city } = props.pool
    let address = ''

    if (number && streetName) {
        address += `${number} ${streetName}`
    } else if (streetName) {
        address += `${streetName}`
    }

    if (city) {
        address += `${address ? ' - ' : ''}${city}`
    }

    return address
})

const globalRating = computed<number>(() => props.pool.rating || 0)

const ratingsCount = computed<number>(() =>
    typeof props.pool.statistic === 'object' &&
    (props.pool.statistic as PoolStatistic)?.ratingsCount
        ? (props.pool.statistic as PoolStatistic)?.ratingsCount
        : (props.pool.statistic as number) || 0
)

const isDisabled = computed<boolean>(() => !props.pool.slug || false)

const cardStyles = computed(() =>
    cva(
        'group flex items-start justify-start flex-col gap-4 py-4 pl-4 border rounded-2xl bg-white h-full',
        {
            variants: {
                isDisabled: {
                    true: 'border-secondary-gray-disabled',
                    false: 'hover:border-light-blue-primary hover:bg-primary-light border-gray-disabled'
                }
            }
        }
    )({
        isDisabled: isDisabled.value
    })
)

const nameStyles = computed(() =>
    cva('text-base font-bold leading-5', {
        variants: {
            isDisabled: {
                true: 'text-gray-disabled',
                false: 'text-header-dark'
            }
        }
    })({
        isDisabled: isDisabled.value
    })
)

const addressStyles = computed(() =>
    cva('text-base font-normal leading-[18px]', {
        variants: {
            isDisabled: {
                true: 'text-gray-disabled',
                false: 'text-gray-l-500 group-hover:text-light-blue-primary'
            }
        }
    })({
        isDisabled: isDisabled.value
    })
)

const iconStyles = computed(() =>
    cva('size-6', {
        variants: {
            isCompatible: {
                true: 'text-light-blue-primary',
                false: 'text-icon-secondary-warning'
            }
        }
    })({
        isCompatible: props.pool.canChargeWithChargemap
    })
)

const getBadgeType = computed<BadgeType>(() =>
    props.pool.canChargeWithChargemap ? BadgeType.INFO : BadgeType.WARNING
)

const chargemapCompatibilityText = computed<string>(() =>
    props.pool.canChargeWithChargemap
        ? t('pass.compatible')
        : t('pass.incompatible')
)
</script>

<template>
    <a :href="poolDetailsUrl" :title="pool.name" class="w-full max-w-[327px]">
        <div :class="cardStyles">
            <div class="gap-4 flex flex-col items-start justify-center pr-4">
                <div class="flex flex-col gap-1 items-start justify-center">
                    <span :class="nameStyles">
                        {{ pool.name }}
                    </span>
                    <span :class="addressStyles">
                        {{ formatAddress }}
                    </span>
                    <PoolRating
                        :rating="globalRating"
                        :rating-count="ratingsCount"
                        :disabled="isDisabled"
                        orientation="horizontal"
                        size="extra-small"
                        responsive
                    />
                </div>
                <ChargemapBadge
                    :type="getBadgeType"
                    :isB2B
                    :custom-icon-class="iconStyles"
                    :disabled="isDisabled"
                >
                    <template #icon>
                        <IconChargingBadge class="size-5" is-solid />
                    </template>
                    <span class="text-[15px]">
                        {{ chargemapCompatibilityText }}
                    </span>
                </ChargemapBadge>
            </div>

            <div class="w-full">
                <SimplifiedConnectorsCarousel
                    v-if="pool.chargingConnectors.length > 0 || loadingStations"
                    :charging-connectors="pool.chargingConnectors"
                    :legacy-api-url="legacyApiUrl"
                    :loading-stations="loadingStations"
                    :disabled="isDisabled"
                    :isB2B
                />
            </div>
        </div>
    </a>
</template>
